import { ClusterPod } from '../../types';

export type EdgePlatformStandardVersion = Record<string, string[]>;

/**
 * Resolves the expected version(s) for a pod image by matching the image name
 * (or its last path segment) against the edgePlatformStandardVersion dictionary keys.
 * The API can report more than one acceptable version per image, so the full
 * list is returned and the pod is considered a match if its version appears
 * anywhere in that list.
 */
export function getExpectedVersion(
  imageName: string | undefined,
  edgePlatformStandardVersion: EdgePlatformStandardVersion = {}
): string[] | undefined {
  if (!imageName) return undefined;

  const normalizedImageName = imageName.split('/').pop()?.toLowerCase() ?? '';
  const matchedKey = Object.keys(edgePlatformStandardVersion).find((key) => {
    const normalizedKey = key.toLowerCase();
    return (
      normalizedImageName === normalizedKey ||
      normalizedImageName.includes(normalizedKey)
    );
  });

  const versions = matchedKey ? edgePlatformStandardVersion[matchedKey] : undefined;
  return versions && versions.length > 0 ? versions : undefined;
}

export function isVersionMismatch(
  pod: ClusterPod,
  edgePlatformStandardVersion: EdgePlatformStandardVersion = {}
): boolean {
  const expectedVersions = getExpectedVersion(
    pod?.imageName,
    edgePlatformStandardVersion
  );
  return !!expectedVersions && !expectedVersions.includes(pod?.imageVersion);
}

export function hasNamespaceVersionMismatch(
  pods: ClusterPod[] = [],
  edgePlatformStandardVersion: EdgePlatformStandardVersion = {}
): boolean {
  return pods.some((pod) =>
    isVersionMismatch(pod, edgePlatformStandardVersion)
  );
}

export type NamespaceVersionStatus = 'mismatch' | 'noBaseline' | 'ok';

/**
 * Namespace-level rollup: a confirmed mismatch on any pod takes priority over
 * an unresolved baseline, which in turn takes priority over a clean match.
 */
export function getNamespaceVersionStatus(
  pods: ClusterPod[] = [],
  edgePlatformStandardVersion: EdgePlatformStandardVersion = {}
): NamespaceVersionStatus {
  if (hasNamespaceVersionMismatch(pods, edgePlatformStandardVersion)) {
    return 'mismatch';
  }
  const hasUnresolvedBaseline = pods.some(
    (pod) => !getExpectedVersion(pod?.imageName, edgePlatformStandardVersion)
  );
  return hasUnresolvedBaseline ? 'noBaseline' : 'ok';
}
